import * as React from 'react';
import { motion } from 'framer-motion';
import { Trophy } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';

export type PrItem = { label: string; rmType: string; value: number; prev: number };

const COLORS = ['#f97316', '#fb923c', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#f472b6'];

// Celebratory overlay shown when the user logs a new all-time-best rep-max.
export default function PrCelebration({ prs, onClose }: { prs: PrItem[]; onClose: () => void }) {
  const { t } = useLocale();

  React.useEffect(() => {
    const id = window.setTimeout(onClose, 4200);
    return () => window.clearTimeout(id);
  }, [onClose]);

  const particles = React.useMemo(
    () =>
      Array.from({ length: 28 }).map((_, i) => ({
        id: i,
        color: COLORS[i % COLORS.length],
        x: (Math.random() * 2 - 1) * 220,
        y: 200 + Math.random() * 240,
        rot: Math.random() * 360,
        delay: Math.random() * 0.15,
        size: 6 + Math.random() * 6,
      })),
    [],
  );

  return (
    <div className="fixed inset-0 z-[1200] grid place-items-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* confetti burst from the centre */}
      <div className="pointer-events-none absolute left-1/2 top-1/2">
        {particles.map((p) => (
          <motion.div
            key={p.id}
            initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
            animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.rot }}
            transition={{ duration: 1.6, delay: p.delay, ease: 'easeOut' }}
            style={{ position: 'absolute', width: p.size, height: p.size, borderRadius: 2, background: p.color }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.6, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        className="relative w-full max-w-xs rounded-3xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-2xl p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          animate={{ rotate: [0, -8, 8, -6, 6, 0] }}
          transition={{ duration: 0.9 }}
          className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-3"
        >
          <Trophy size={30} className="text-amber-500" />
        </motion.div>
        <div className="text-lg font-black">
          {prs.length > 1 ? `${prs.length} ${t('new PRs!', 'new PRs!')}` : t('New PR!', 'New PR!')}
        </div>
        <div className="mt-2 space-y-1">
          {prs.slice(0, 4).map((p, i) => (
            <div key={i} className="text-sm">
              <span className="font-semibold">{p.label}</span>{' '}
              <span className="opacity-50">{p.rmType.toUpperCase()}</span>{' '}
              <span className="font-bold text-brand-600 dark:text-brand-400">{p.value} kg</span>
              {p.prev > 0 && (
                <span className="opacity-40 text-xs"> (+{Math.round((p.value - p.prev) * 10) / 10} kg)</span>
              )}
            </div>
          ))}
        </div>
        <button onClick={onClose} className="mt-4 text-xs font-medium opacity-50 hover:opacity-80">
          {t('Tap to dismiss', 'Tap to dismiss')}
        </button>
      </motion.div>
    </div>
  );
}
